import { useEffect, useState } from "react";
import { create } from "zustand";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";
import { clsx } from "clsx";

type ToastType = "success" | "error" | "warning" | "info";

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastStore {
  toasts: Toast[];
  addToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

let nextId = 0;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  addToast: (type, message, duration = 5000) => {
    const id = String(++nextId);
    set((s) => ({ toasts: [...s.toasts, { id, type, message, duration }] }));
  },
  removeToast: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));

/** Convenience helpers – callable from anywhere (components, services, etc.) */
export const toast = {
  success: (msg: string, duration?: number) => useToastStore.getState().addToast("success", msg, duration),
  error: (msg: string, duration?: number) => useToastStore.getState().addToast("error", msg, duration ?? 8000),
  warning: (msg: string, duration?: number) => useToastStore.getState().addToast("warning", msg, duration),
  info: (msg: string, duration?: number) => useToastStore.getState().addToast("info", msg, duration),
};

const icons: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const styles: Record<ToastType, string> = {
  success: "border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300",
  error: "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300",
  warning: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  info: "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
};

const iconStyles: Record<ToastType, string> = {
  success: "text-green-500 dark:text-green-400",
  error: "text-red-500 dark:text-red-400",
  warning: "text-amber-500 dark:text-amber-400",
  info: "text-blue-500 dark:text-blue-400",
};

function ToastItem({ toast: t, onRemove }: { toast: Toast; onRemove: () => void }) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!t.duration) return;
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(onRemove, 200);
    }, t.duration);
    return () => clearTimeout(timer);
  }, [t.duration, onRemove]);

  const Icon = icons[t.type];

  return (
    <div
      className={clsx(
        "pointer-events-auto flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg transition-all duration-200",
        styles[t.type],
        exiting ? "translate-x-full opacity-0" : "translate-x-0 opacity-100",
      )}
    >
      <Icon className={clsx("mt-0.5 h-5 w-5 flex-shrink-0", iconStyles[t.type])} />
      <p className="flex-1 text-sm font-medium">{t.message}</p>
      <button
        onClick={() => { setExiting(true); setTimeout(onRemove, 200); }}
        className="flex-shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Render this once at the app root level */
export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[100] flex flex-col gap-2 w-96 max-w-[calc(100vw-2rem)]">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onRemove={() => removeToast(t.id)} />
      ))}
    </div>
  );
}
